/**
 * Stale-deploy recovery.
 *
 * With route-based code-splitting, every page is a content-hashed JS chunk. When
 * a new build is deployed, the old chunk filenames disappear from the server. A
 * browser still running the previous app shell (an open tab, or a cached
 * index.html) then requests a chunk that 404s — and because the SPA host falls
 * back to index.html, the browser receives HTML where it expected JavaScript
 * and throws a module/MIME error.
 *
 * The only correct recovery is a full reload: it fetches the current index.html
 * and the current chunk hashes. This module centralises (a) recognising those
 * errors and (b) reloading at most once per short window so a genuinely broken
 * deploy can't cause an infinite reload loop.
 */

const LAST_RELOAD_KEY = 'projexhq:lastChunkReload';
/** Don't auto-reload again within this window — if the error persists right
 *  after a reload, it isn't a stale chunk, so show the manual fallback. */
const RELOAD_COOLDOWN_MS = 10_000;

/** Heuristic: does this error look like a failed dynamic import / stale chunk?
 *  Matched loosely across browsers — a false positive just triggers a reload,
 *  which is harmless (and rate-limited). */
export function looksLikeChunkError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : '';
  return (
    name === 'ChunkLoadError' ||
    /loading chunk [\d]+ failed/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg) ||
    // Safari when a module request returns HTML (the SPA fallback):
    /is not a valid javascript mime type/i.test(msg) ||
    // Chrome/Edge equivalent:
    /expected a javascript(-or-wasm)? module script/i.test(msg) ||
    /failed to load module script/i.test(msg)
  );
}

/**
 * Reload once to pick up the freshly deployed shell. Returns true if a reload
 * was triggered, false if we reloaded too recently (caller should then let the
 * error surface so the user gets a manual option instead of a reload loop).
 */
export function reloadForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked (private mode etc.) — best-effort reload anyway.
  }
  window.location.reload();
  return true;
}
